/**
 * OR-6504: the inline chat card for a parameterized variable GROUP.
 *
 * Deliberately as spare as the per-member {@link AssetCard}: kind row + status
 * chip, the group's name, and `View details ›`. The review payload — the shared
 * definition, the numbered values, the alias pattern, the output shape and the
 * mapping pool — belongs on the group's details page, not here (Product mockups,
 * 2026-07-28). An earlier build put all of it on the card; that made the chat
 * panel carry a wall of text where every other asset gets three lines.
 *
 * Two differences from `AssetCard`, both because NOTHING has been created at
 * this point (§22.1) — approval is what creates the variables:
 *
 * - **No timestamp.** There is no creation time to show.
 * - **The kind row reads "Variable group · N variables"**, because the thing
 *   being reviewed is the group, and the count is the whole point of it.
 *
 * The status chip says Draft: not because a draft exists — none does — but
 * because that is the stage the whole set is at, and it is what each member's
 * card will show once they are created.
 */

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FunctionsIcon from '@mui/icons-material/Functions';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { ArtifactStatusChip } from '@verantos-dev/shared-components';
import { borderRadius, shadows } from '@verantos-dev/shared-ui-components/tokens';

import type { EpiVariableFamily } from '../types/epi-chat-types';

export interface VariableFamilyGroupCardProps {
  family: EpiVariableFamily;
  /**
   * Opens the group's details page. Omitted where no navigation is wired (the
   * card then renders without the click-through rather than dead-ending on a
   * chevron that does nothing).
   */
  onOpenDetails?: () => void;
}

export const VariableFamilyGroupCard = ({
  family,
  onOpenDetails,
}: VariableFamilyGroupCardProps) => {
  const intl = useIntl();
  const kindLabel = intl.formatMessage(
    {
      id: 'epiChat.variableFamily.groupKindLabel',
      defaultMessage: 'Variable group · {count} variables',
    },
    { count: family.memberCount }
  );

  const body = (
    <>
      <Stack direction="row" alignItems="center" spacing={4}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <FunctionsIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} aria-hidden />
          <Typography variant="body2" color="text.secondary" noWrap>
            {kindLabel}
          </Typography>
        </Stack>
        <ArtifactStatusChip status="Draft" data-testid="variable-family-group-card-status-chip" />
      </Stack>
      <Typography
        variant="body1"
        data-testid="variable-family-title"
        sx={{ fontWeight: 500, color: 'text.primary', minWidth: 0 }}
        noWrap
      >
        {family.displayName}
      </Typography>
      {onOpenDetails && (
        <Stack direction="row" alignItems="center" justifyContent="flex-end">
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flexShrink: 0, color: 'primary.main' }}
          >
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {intl.formatMessage({
                id: 'epiChat.assetCard.viewDetails',
                defaultMessage: 'View details',
              })}
            </Typography>
            <ChevronRightIcon sx={{ fontSize: 16 }} aria-hidden />
          </Stack>
        </Stack>
      )}
    </>
  );

  const surface = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '100%',
    gap: 2,
    padding: 4,
    textAlign: 'left',
    backgroundColor: 'background.paper',
    borderLeft: (theme: { palette: { primary: { main: string } } }) =>
      `3px solid ${theme.palette.primary.main}`,
    borderRadius: `${borderRadius.sm}px`,
    boxShadow: shadows.sm,
  } as const;

  return (
    <Box sx={{ marginY: 2.5, marginX: { xs: 0, sm: 2 } }}>
      {onOpenDetails ? (
        <ButtonBase
          onClick={onOpenDetails}
          data-testid="variable-family-group-card"
          aria-label={intl.formatMessage(
            {
              id: 'epiChat.variableFamily.groupCardAriaLabel',
              defaultMessage: 'Open {name}, a group of {count} variables',
            },
            { name: family.displayName, count: family.memberCount }
          )}
          sx={{
            ...surface,
            transition: 'box-shadow 0.15s',
            '&:hover': { boxShadow: shadows.md },
            '&:focus-visible': {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          {body}
        </ButtonBase>
      ) : (
        <Box data-testid="variable-family-group-card" sx={surface}>
          {body}
        </Box>
      )}
    </Box>
  );
};
